import { useMemo } from "react";

import { Skeleton, Stack, type SxProps, type Theme, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";

import CountUpCard from "../../../../../Components/CountUp/CountUpCard";
import { roundToPrecision } from "../../../../../utils/numbers";
import InfoIconWithTooltip from "../../../Components/InfoIconWithTooltip";
import { useTrackK8sEventOnce } from "../../../events";
import { K8S_LENS_GLOSSARY } from "../../../messages";
import { TimeRange } from "../../ClusterDetail/Components/TimeRangeSelector";
import CardTitle from "../Components/CardTitle";
import { useClusterDashboardContext } from "../Table/context";

const boxSx: SxProps<Theme> = {
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
};

const cardSx: SxProps<Theme> = {
  height: "100%",
  display: "flex",
  flexDirection: "column",
};

const commonCountupProps = {
  sx: { ...boxSx },
  cardSx: { ...cardSx },
  duration: 1,
  decimals: 2,
  text: "",
  contentStyle: { fontSize: 36 },
  spacing: 1,
  prefix: "$",
};

const calculatePercentage = (current: number, previous: number) => {
  if (previous === 0) {
    return current > 0 ? 100 : 0;
  }
  return parseFloat(((current - previous) / previous).toFixed(2)) * 100;
};

const Fsk8sOverviewDashboard = () => {
  const { dashboardClusters, loading, timeRange } = useClusterDashboardContext();

  const {
    waste: psWaste,
    previousWaste: psPreviousWaste,
    cost: psCost,
    previousCost: psPreviousCost,
  } = dashboardClusters.summary ?? {
    waste: 0,
    previousWaste: 0,
    cost: 0,
    previousCost: 0,
  };

  const totalDoitCost = useMemo(
    () =>
      dashboardClusters.clusters.reduce((acc: number, cluster) => {
        const doitCost = Number(cluster.doitBillingCosts?.[timeRange].cost ?? 0);
        return acc + doitCost;
      }, 0),
    [dashboardClusters.clusters, timeRange]
  );

  const previousDoitCost = useMemo(
    () =>
      dashboardClusters.clusters.reduce((acc: number, cluster) => {
        const doitCost = Number(cluster.doitBillingCosts?.[timeRange].previousCost ?? 0);
        return acc + doitCost;
      }, 0),
    [dashboardClusters.clusters, timeRange]
  );

  const doitWaste = (psWaste / psCost) * totalDoitCost || 0;
  const doitPreviousWaste = (psPreviousWaste / psPreviousCost) * previousDoitCost || 0;

  useTrackK8sEventOnce({
    name: "K8s Dashboard Viewed",
    properties: {
      timeRange: timeRange ?? TimeRange.T_30D,
      totalSpend: totalDoitCost || 0,
      podWaste: doitWaste || 0,
      totalClusters: dashboardClusters.clusters.length,
    },
    condition: !loading && !!timeRange && !!dashboardClusters.clusters.length,
  });

  if (loading) {
    return <Skeleton variant="rectangular" height={172} />;
  }

  const previousCostPercentage = calculatePercentage(totalDoitCost, previousDoitCost);
  const previousWastePercentage = calculatePercentage(doitWaste, doitPreviousWaste);

  return (
    <Grid container spacing={2} minHeight={172}>
      <Grid size={{ xs: 12, sm: 6, md: 6 }}>
        <CountUpCard
          {...commonCountupProps}
          childAboveContent={
            <CardTitle>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h4">Total spend</Typography>
                <InfoIconWithTooltip tooltip={K8S_LENS_GLOSSARY.TOTAL_SPEND} />
              </Stack>
            </CardTitle>
          }
          number={totalDoitCost}
          decimals={2}
          childBelowText={
            <Typography variant="body2" color="text.secondary">
              {previousCostPercentage < 0 ? "Down" : "Up"} {roundToPrecision(Math.abs(previousCostPercentage), 4)}% vs.
              last period
            </Typography>
          }
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 6 }}>
        <CountUpCard
          {...commonCountupProps}
          childAboveContent={
            <CardTitle>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h4">Pod waste</Typography>
                <InfoIconWithTooltip tooltip={K8S_LENS_GLOSSARY.POD_WASTE} />
              </Stack>
            </CardTitle>
          }
          number={doitWaste}
          decimals={2}
          childBelowText={
            <Typography variant="body2" color="text.secondary">
              {previousWastePercentage < 0 ? "Down" : "Up"} {roundToPrecision(Math.abs(previousWastePercentage), 4)}%
              vs. last period
            </Typography>
          }
        />
      </Grid>
    </Grid>
  );
};

export default Fsk8sOverviewDashboard;
